import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bookmark,
  Check,
  ImageOff,
  AlertCircle,
  Layers,
  Plus,
  Tags,
  History,
  ChevronDown,
} from "lucide-react";
import dayjs from "dayjs";
import { toggleProductConfidence, toggleThumb } from "../../../api/feed";
import { useUserContext } from "../../../context/UserContext";
import LoadingSpinner from "../../Universal/LoadingSpinner";

const CONFIDENCE_OPTIONS = [
  {
    key: "NS",
    action: "ns",
    label: "No Signal",
    aliases: ["ns", "no signal", "no_signal", "none", "null", ""],
    level: 0,
    angle: -90,
    dash: 0,
  },
  {
    key: "low",
    action: "low",
    label: "Low",
    aliases: ["low"],
    level: 1,
    angle: -30,
    dash: 33,
  },
  {
    key: "medium",
    action: "medium",
    label: "Medium",
    aliases: ["medium", "med"],
    level: 2,
    angle: 30,
    dash: 66,
  },
  {
    key: "high",
    action: "high",
    label: "High",
    aliases: ["high"],
    level: 3,
    angle: 90,
    dash: 100,
  },
];

const cn = (...classes) => classes.filter(Boolean).join(" ");

const formatLabel = (value) =>
  String(value ?? "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatFlag = (value) =>
  String(value ?? "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase()
    .trim();

const isDarkMode = () => {
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("dark");
};

const getConfidenceColors = (dark) => ({
  NS: {
    active: dark ? "rgba(148,163,184,0.72)" : "rgba(100,116,139,0.66)",
    soft: dark ? "rgba(148,163,184,0.1)" : "rgba(100,116,139,0.08)",
    border: dark ? "rgba(148,163,184,0.2)" : "rgba(100,116,139,0.16)",
    text: dark ? "rgba(203,213,225,0.88)" : "rgba(71,85,105,0.9)",
  },
  low: {
    active: dark ? "rgba(74,222,128,0.92)" : "rgba(22,163,74,0.88)",
    soft: dark ? "rgba(34,197,94,0.13)" : "rgba(22,163,74,0.09)",
    border: dark ? "rgba(74,222,128,0.24)" : "rgba(22,163,74,0.18)",
    text: dark ? "rgba(187,247,208,0.95)" : "rgba(21,128,61,0.96)",
  },
  medium: {
    active: dark ? "rgba(250,204,21,0.92)" : "rgba(202,138,4,0.88)",
    soft: dark ? "rgba(234,179,8,0.14)" : "rgba(202,138,4,0.1)",
    border: dark ? "rgba(250,204,21,0.27)" : "rgba(202,138,4,0.2)",
    text: dark ? "rgba(254,240,138,0.96)" : "rgba(161,98,7,0.96)",
  },
  high: {
    active: dark ? "rgba(248,113,113,0.92)" : "rgba(220,38,38,0.86)",
    soft: dark ? "rgba(239,68,68,0.14)" : "rgba(220,38,38,0.09)",
    border: dark ? "rgba(248,113,113,0.28)" : "rgba(220,38,38,0.18)",
    text: dark ? "rgba(254,202,202,0.96)" : "rgba(185,28,28,0.96)",
  },
});

const getPalette = (t) => {
  const dark = isDarkMode();

  const accent = "#1c9dea";
  const accentSoft = "rgba(14,165,233,0.1)";
  const accentBorder = "rgba(14,165,233,0.24)";
  const accentText = dark ? "rgba(125,211,252,0.95)" : accent;

  return {
    dark,
    accent,

    cardBg:
      t?.cardBg ||
      (dark
        ? "linear-gradient(135deg, rgba(15,23,42,0.92), rgba(2,6,23,0.96))"
        : "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))"),
    cardBorder:
      t?.cardBorder || (dark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.1)"),
    cardShadow:
      t?.cardShadow ||
      (dark
        ? "0 16px 42px rgba(2,6,23,0.26)"
        : "0 16px 36px rgba(15,23,42,0.08)"),

    inputBg:
      t?.inputBg || (dark ? "rgba(15,23,42,0.44)" : "rgba(248,250,252,0.86)"),
    inputBorder:
      t?.inputBorder ||
      (dark ? "rgba(148,163,184,0.13)" : "rgba(15,23,42,0.1)"),

    activeBg: t?.activeBg || accentSoft,
    activeBorder: t?.activeBorder || accentBorder,
    activeText: t?.activeText || accentText,

    textPrimary:
      t?.textPrimary ||
      (dark ? "rgba(248,250,252,0.96)" : "rgba(15,23,42,0.94)"),
    textSecondary:
      t?.textSecondary ||
      (dark ? "rgba(226,232,240,0.78)" : "rgba(51,65,85,0.88)"),
    textMuted:
      t?.textMuted ||
      (dark ? "rgba(148,163,184,0.62)" : "rgba(100,116,139,0.72)"),

    divider:
      t?.divider || (dark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.1)"),

    screenshotFallback: dark ? "rgba(15,23,42,0.52)" : "rgba(241,245,249,0.9)",
    imageGradient: dark
      ? "linear-gradient(180deg, transparent, rgba(2,6,23,0.72))"
      : "linear-gradient(180deg, transparent, rgba(15,23,42,0.22))",

    meterShellBg: dark ? "rgba(2,6,23,0.78)" : "rgba(255,255,255,0.9)",
    meterShellBorder: dark ? "rgba(56,189,248,0.24)" : "rgba(14,165,233,0.18)",
    meterBase: dark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.16)",
    meterBaseSoft: dark ? "rgba(148,163,184,0.1)" : "rgba(100,116,139,0.1)",
    meterNeedle: dark ? "rgba(226,232,240,0.92)" : "rgba(15,23,42,0.84)",
    meterKnob: dark ? "rgba(2,6,23,0.96)" : "rgba(255,255,255,0.98)",

    flagBg: dark ? "rgba(14,165,233,0.075)" : "rgba(14,165,233,0.065)",
    flagBorder: dark ? "rgba(56,189,248,0.18)" : "rgba(14,165,233,0.16)",
    flagText: dark ? "rgba(186,230,253,0.9)" : "rgba(2,132,199,0.94)",

    classificationBg: dark ? "rgba(14,165,233,0.12)" : "rgba(14,165,233,0.08)",
    classificationBorder: dark
      ? "rgba(56,189,248,0.26)"
      : "rgba(14,165,233,0.2)",
    classificationText: dark
      ? "rgba(224,242,254,0.96)"
      : "rgba(2,132,199,0.96)",

    confidenceColors: getConfidenceColors(dark),
  };
};

const getConfidenceOption = (value) => {
  const normalized = String(value ?? "")
    .toLowerCase()
    .replace(/-/g, "_")
    .trim();

  return (
    CONFIDENCE_OPTIONS.find((option) => option.aliases.includes(normalized)) ||
    CONFIDENCE_OPTIONS[0]
  );
};

const getConfidenceFromLevel = (level) => {
  const clamped = Math.max(0, Math.min(3, Number(level) || 0));

  return (
    CONFIDENCE_OPTIONS.find((option) => option.level === clamped) ||
    CONFIDENCE_OPTIONS[0]
  );
};

const getTierLabelValue = (article = {}) => article.tier_label ?? "NS";

const getScreenshot = (article) =>
  article.screenshot_full ||
  article.screenshotFull ||
  article.screenshot ||
  article.image_url ||
  article.imageUrl ||
  article.thumbnail ||
  (Array.isArray(article.product_img) ? article.product_img[0] : null) ||
  null;

const getArticle = (article = {}) => {
  const tier = getConfidenceOption(getTierLabelValue(article));

  return {
    ...article,
    flags: Array.isArray(article.flags) ? article.flags : [],
    feedback: Array.isArray(article.feedback)
      ? article.feedback.filter(
          (entry) => typeof entry === "string" && entry.trim(),
        )
      : [],
    tier_label: tier.key,
    confidenceLabel: tier.label,
    classification:
      article.classification || article.target_type || article.type || "",
    recommended_action:
      article.recommended_action || article.recommendedAction || "",
    reasoning: article.reasoning || article.why_shown || article.whyShown || "",
  };
};

const getTargetType = (article = {}) =>
  String(article.target_type || article.targetType || "")
    .toLowerCase()
    .trim();

const canAdjustConfidence = (article = {}) =>
  getTargetType(article) === "ecommerce";

const FlagBadge = ({ children, p }) => (
  <span
    className="inline-flex h-[23px] max-w-full items-center gap-1.5 rounded-md px-2 text-[11px] leading-none tracking-[0.01em]"
    style={{
      color: p.flagText,
      background: p.flagBg,
      border: `1px solid ${p.flagBorder}`,
    }}
  >
    <Tags size={11} style={{ flexShrink: 0, opacity: 0.72 }} />
    <span className="truncate">{children}</span>
  </span>
);

const ClassificationBadge = ({ value, p }) => {
  if (!value) return null;

  return (
    <div
      className="inline-flex h-[24px] max-w-full items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium leading-none tracking-[0.01em]"
      style={{
        color: p.classificationText,
        background: p.classificationBg,
        border: `1px solid ${p.classificationBorder}`,
      }}
    >
      <Layers size={12} style={{ flexShrink: 0, opacity: 0.82 }} />
      <span className="truncate">{formatLabel(value)}</span>
    </div>
  );
};

const MeterToast = ({ show, p, error, message = "Updated" }) => (
  <div
    className={cn(
      "pointer-events-none absolute bottom-[96px] left-[218px] z-40 rounded-lg px-2.5 py-1.5 text-[11px] backdrop-blur-md transition-all duration-200",
      show ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
    )}
    style={{
      color: error
        ? p.dark
          ? "rgba(254,202,202,0.96)"
          : "rgba(185,28,28,0.94)"
        : p.activeText,
      background: p.dark ? "rgba(2,6,23,0.78)" : "rgba(255,255,255,0.88)",
      border: `1px solid ${
        error
          ? p.dark
            ? "rgba(248,113,113,0.28)"
            : "rgba(220,38,38,0.18)"
          : p.activeBorder
      }`,
      boxShadow: p.dark
        ? "0 14px 30px rgba(2,6,23,0.42), 0 0 18px rgba(28,157,234,0.12)"
        : "0 14px 26px rgba(15,23,42,0.14)",
    }}
  >
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      {error ? <AlertCircle size={12} /> : <Check size={12} />}
      {error ? "Update failed" : message}
    </span>
  </div>
);

const BookmarkButton = ({ active, p, onClick }) => (
  <button
    type="button"
    title={active ? "Remove from catalog" : "Add to catalog"}
    aria-label={active ? "Remove from catalog" : "Add to catalog"}
    className={cn(
      "absolute right-3 top-3 z-30 inline-flex h-9 w-9 items-center justify-center rounded-xl backdrop-blur-md transition-all",
      active ? "opacity-100" : "opacity-0 group-hover:opacity-100",
    )}
    style={{
      color: active ? p.activeText : p.textSecondary,
      background: active
        ? p.activeBg
        : p.dark
          ? "rgba(2,6,23,0.62)"
          : "rgba(255,255,255,0.72)",
      border: `1px solid ${active ? p.activeBorder : p.inputBorder}`,
      boxShadow: active
        ? "0 10px 26px rgba(14,165,233,0.16)"
        : "0 10px 24px rgba(2,6,23,0.14)",
    }}
    onClick={onClick}
  >
    <Bookmark size={16} fill={active ? "currentColor" : "none"} />
  </button>
);

const DetailBlock = ({ icon: Icon, label, children, p, strong }) => (
  <div
    className="rounded-xl p-3"
    style={{
      background: strong ? p.activeBg : p.inputBg,
      border: `1px solid ${strong ? p.activeBorder : p.inputBorder}`,
    }}
  >
    <div
      className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em]"
      style={{
        color: strong ? p.activeText : p.textMuted,
      }}
    >
      <Icon size={12} />
      {label}
    </div>

    <p
      className="line-clamp-3 text-sm leading-relaxed"
      style={{
        color: strong ? p.textPrimary : p.textSecondary,
      }}
    >
      {children}
    </p>
  </div>
);

// "<unix ts>. First sentence is the confidence action. Second sentence is the
// user's note (if any)."
const buildFeedbackEntry = (draft, note = "", userName = "User") => {
  const timestamp = Math.floor(Date.now() / 1000);
  const previous = getConfidenceOption(draft.previousTier);
  const next = getConfidenceOption(draft.nextTier);

  const sentence =
    draft.mode === "note_only"
      ? `${userName} added a note at ${next.label} confidence.`
      : `${userName} moved confidence from ${previous.label} to ${next.label}.`;

  const trimmedNote = note.trim();

  return `${timestamp}. ${sentence}${trimmedNote ? ` ${trimmedNote}` : ""}`;
};

const HISTORY_COLLAPSED_COUNT = 3;

// Entries are normalized as "<unix ts>. <action sentence> <note?>" — pull the
// timestamp out so it can render as a label, and break the action sentence
// into structured parts so the tiers can render as colored pills.
const TIER_LABEL_PATTERN = CONFIDENCE_OPTIONS.map((o) => o.label).join("|");

const MOVE_SENTENCE_RE = new RegExp(
  `^(.+?) moved confidence from (${TIER_LABEL_PATTERN}) to (${TIER_LABEL_PATTERN})\\.\\s*(.*)$`,
);

const NOTE_SENTENCE_RE = new RegExp(
  `^(.+?) added a note at (${TIER_LABEL_PATTERN}) confidence\\.\\s*(.*)$`,
);

const parseHistoryEntry = (entry) => {
  let text = String(entry).trim();
  let timestamp = null;

  const tsMatch = /^(\d{9,12})\.\s*(.*)$/.exec(text);

  if (tsMatch) {
    const date = dayjs.unix(Number(tsMatch[1]));
    timestamp = date.isValid() ? date.format("MMM D, YYYY · h:mm A") : null;
    text = tsMatch[2];
  }

  const moveMatch = MOVE_SENTENCE_RE.exec(text);

  if (moveMatch) {
    return {
      kind: "move",
      timestamp,
      user: moveMatch[1],
      fromTier: moveMatch[2],
      toTier: moveMatch[3],
      note: moveMatch[4].trim(),
    };
  }

  const noteMatch = NOTE_SENTENCE_RE.exec(text);

  if (noteMatch) {
    return {
      kind: "note_add",
      timestamp,
      user: noteMatch[1],
      atTier: noteMatch[2],
      note: noteMatch[3].trim(),
    };
  }

  return { kind: "text", timestamp, text };
};

const HistoryBlock = ({ entries, p }) => {
  const [expanded, setExpanded] = useState(false);
  const count = Array.isArray(entries) ? entries.length : 0;
  const prevCountRef = useRef(count);
  const [animateNewest, setAnimateNewest] = useState(false);

  // Slide the newest entry in when one is added during the session; entries
  // present on first render just appear.
  useEffect(() => {
    if (count > prevCountRef.current) {
      setAnimateNewest(true);
      prevCountRef.current = count;

      const timer = window.setTimeout(() => setAnimateNewest(false), 360);
      return () => window.clearTimeout(timer);
    }

    prevCountRef.current = count;
    return undefined;
  }, [count]);

  if (count === 0) return null;

  // Entries arrive oldest-first; show the most recent change at the top.
  const ordered = [...entries].reverse().map(parseHistoryEntry);
  const visible = expanded
    ? ordered
    : ordered.slice(0, HISTORY_COLLAPSED_COUNT);
  const hiddenCount = ordered.length - HISTORY_COLLAPSED_COUNT;

  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: p.inputBg,
        border: `1px solid ${p.inputBorder}`,
      }}
    >
      <ul className="flex flex-col gap-2.5">
        {visible.map((entry, index) => (
          <li
            key={`${index}-${entry.timestamp || entry.text || entry.note}`}
            className={cn(
              "flex items-start gap-2",
              index === 0 && animateNewest && "history-entry-enter",
            )}
          >
            <span
              className="mt-[5px] h-1 w-1 shrink-0 rounded-full"
              style={{
                background: index === 0 ? p.activeText : p.textMuted,
                opacity: index === 0 ? 0.9 : 0.55,
              }}
            />

            <div className="min-w-0 flex-1">
              {entry.timestamp && (
                <div
                  className="mb-1 text-[10px] leading-tight tracking-[0.04em]"
                  style={{ color: p.textMuted }}
                >
                  {entry.timestamp}
                </div>
              )}

              {entry.kind === "move" && (
                <div
                  className="flex flex-wrap items-center gap-1.5 text-[12px]"
                  style={{ color: p.textSecondary }}
                >
                  <span>{entry.user} moved</span>
                  <ConfidenceStatusPill value={entry.fromTier} p={p} />
                  <span style={{ color: p.textMuted }}>→</span>
                  <ConfidenceStatusPill value={entry.toTier} p={p} />
                </div>
              )}

              {entry.kind === "note_add" && (
                <div
                  className="flex flex-wrap items-center gap-1.5 text-[12px]"
                  style={{ color: p.textSecondary }}
                >
                  <span>{entry.user} added a note at</span>
                  <ConfidenceStatusPill value={entry.atTier} p={p} />
                </div>
              )}

              {entry.kind === "text" && (
                <p
                  className="break-words text-[12px] leading-relaxed"
                  style={{ color: p.textSecondary }}
                >
                  {entry.text}
                </p>
              )}

              {entry.note && (
                <p
                  className="mt-1 break-words text-[12px] leading-relaxed"
                  style={{ color: p.textSecondary }}
                >
                  <span style={{ color: p.textMuted }}>Note:</span>{" "}
                  {entry.note}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {hiddenCount > 0 && (
        <button
          type="button"
          className="mt-1.5 text-xs transition-opacity hover:opacity-80"
          style={{ color: p.activeText }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Show less" : `Show all (+${hiddenCount})`}
        </button>
      )}
    </div>
  );
};

// Bottom-right counterpart to the classification pill: same shape, neutral
// palette so it reads as a control. Toggles the collapsible history section.
const HistoryTrigger = ({ count, open, p, onToggle }) => {
  const hasHistory = count > 0;

  return (
    <button
      type="button"
      disabled={!hasHistory}
      aria-expanded={open}
      aria-label={
        hasHistory
          ? `${open ? "Hide" : "Show"} confidence reviews (${count})`
          : "Not yet reviewed"
      }
      className="inline-flex h-[24px] shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium leading-none tracking-[0.01em] transition-colors disabled:cursor-default"
      style={{
        color: !hasHistory
          ? p.textMuted
          : open
            ? p.activeText
            : p.textSecondary,
        background: open ? p.activeBg : p.inputBg,
        border: `1px solid ${open ? p.activeBorder : p.inputBorder}`,
        opacity: hasHistory ? 1 : 0.7,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <History size={12} style={{ flexShrink: 0, opacity: 0.82 }} />

      {hasHistory ? (
        <>
          <span className="tabular-nums">{count}</span>
          <span>{count === 1 ? "review" : "reviews"}</span>
          <ChevronDown
            size={11}
            style={{
              flexShrink: 0,
              opacity: 0.7,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 200ms ease",
            }}
          />
        </>
      ) : (
        <span>Not yet reviewed</span>
      )}
    </button>
  );
};

const getFeedbackPlaceholder = (draft) => {
  if (!draft) return "What should the model learn from this correction?";

  if (draft.mode === "note_only") {
    return "Add context for why this confidence is right, wrong, or worth flagging.";
  }

  const previous = getConfidenceOption(draft.previousTier);
  const next = getConfidenceOption(draft.nextTier);

  if (next.key === "NS") return "Why is this not a signal?";
  if (previous.key === "NS") return "What signal did the model miss?";
  if (next.level > previous.level) {
    return "What makes this more important than the model thought?";
  }
  if (next.level < previous.level) return "Why is this less relevant?";

  return "What should the model learn from this correction?";
};

const ConfidenceStatusPill = ({ value, label, p }) => {
  const option = getConfidenceOption(value);
  const colors = p.confidenceColors[option.key] || p.confidenceColors.NS;

  return (
    <span
      className="inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-medium leading-none"
      style={{
        color: colors.text,
        background: colors.soft,
        border: `1px solid ${colors.border}`,
      }}
    >
      {label || option.label}
    </span>
  );
};

const ConfidenceFeedbackComposer = ({
  draft,
  note,
  p,
  isSaving,
  composerRef,
  onChange,
  onDismiss,
  onSubmit,
}) => {
  if (!draft) return null;

  const isNoteOnly = draft.mode === "note_only";
  const composerBorder = isNoteOnly ? p.inputBorder : p.activeBorder;
  const composerShadow = isNoteOnly
    ? p.dark
      ? "0 16px 34px rgba(2,6,23,0.44)"
      : "0 16px 28px rgba(15,23,42,0.12)"
    : p.dark
      ? "0 16px 36px rgba(2,6,23,0.48), 0 0 18px rgba(28,157,234,0.12)"
      : "0 16px 30px rgba(15,23,42,0.14)";

  return (
    <form
      ref={composerRef}
      className="absolute bottom-[92px] left-3 right-3 z-30 rounded-xl p-2.5 backdrop-blur-md sm:right-auto sm:w-[360px]"
      style={{
        background: p.dark ? "rgba(2,6,23,0.9)" : "rgba(255,255,255,0.95)",
        border: `1px solid ${composerBorder}`,
        boxShadow: composerShadow,
      }}
      onClick={(event) => event.stopPropagation()}
      onSubmit={onSubmit}
    >
      {!isNoteOnly && (
        <div className="mb-2 flex items-center gap-1.5 text-[11px]">
          <span style={{ color: p.textPrimary }}>Updated</span>
          <ConfidenceStatusPill
            value={draft.previousTier}
            label={draft.previousLabel}
            p={p}
          />
          <span style={{ color: p.textMuted }}>→</span>
          <ConfidenceStatusPill
            value={draft.nextTier}
            label={draft.nextLabel}
            p={p}
          />
        </div>
      )}

      <textarea
        value={note}
        rows={2}
        maxLength={280}
        placeholder={getFeedbackPlaceholder(draft)}
        autoFocus
        className="w-full resize-none rounded-lg px-2.5 py-2 text-[12px] leading-relaxed outline-none placeholder:opacity-80"
        style={{
          color: p.textPrimary,
          background: p.dark ? "rgba(15,23,42,0.72)" : p.inputBg,
          border: `1px solid ${p.inputBorder}`,
        }}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onDismiss();
          }
        }}
      />

      <div className="mt-2 flex items-center justify-end gap-1.5">
        <button
          type="button"
          className="rounded-lg px-2 py-1 text-[11px] transition-opacity hover:opacity-85"
          style={{ color: p.textSecondary }}
          onClick={onDismiss}
        >
          {isNoteOnly ? "Cancel" : "Skip"}
        </button>

        <button
          type="submit"
          disabled={isSaving || !note.trim()}
          className="rounded-lg px-2.5 py-1 text-[11px] transition-opacity disabled:cursor-default disabled:opacity-45"
          style={{
            color: isNoteOnly
              ? p.textSecondary
              : p.dark
                ? "rgba(240,249,255,0.98)"
                : p.activeText,
            background: isNoteOnly
              ? p.dark
                ? "rgba(15,23,42,0.72)"
                : "rgba(248,250,252,0.92)"
              : p.activeBg,
            border: `1px solid ${isNoteOnly ? p.inputBorder : p.activeBorder}`,
          }}
        >
          {isSaving ? "Saving..." : "Save note"}
        </button>
      </div>
    </form>
  );
};

const ConfidenceMeter = ({ value, p, onChange, onAddNote, shellRef }) => {
  const meterRef = useRef(null);
  const [hoveredConfidence, setHoveredConfidence] = useState(null);
  const [isNoteButtonHovered, setIsNoteButtonHovered] = useState(false);

  const current = getConfidenceOption(value);
  const hovered = hoveredConfidence
    ? getConfidenceOption(hoveredConfidence)
    : null;

  const confidenceColor =
    p.confidenceColors[current.key] || p.confidenceColors.NS;

  const activeColor =
    current.key === "NS" ? p.textMuted : confidenceColor.active;

  const pivot = { x: 54, y: 45 };
  const needleLength = 28;
  const markRadius = 37;

  const pointFromAngle = (angle, radius) => {
    const rad = ((angle - 90) * Math.PI) / 180;

    return {
      x: pivot.x + Math.cos(rad) * radius,
      y: pivot.y + Math.sin(rad) * radius,
    };
  };

  const needleEnd = pointFromAngle(current.angle, needleLength);

  const commitConfidence = (tierLabelKey) => {
    const option = getConfidenceOption(tierLabelKey);
    onChange(option.key);
  };

  const updateFromClientX = (clientX) => {
    const node = meterRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
    const ratio = x / rect.width;

    if (ratio < 0.25) commitConfidence("NS");
    else if (ratio < 0.5) commitConfidence("low");
    else if (ratio < 0.75) commitConfidence("medium");
    else commitConfidence("high");
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();

    updateFromClientX(e.clientX);

    const handleMove = (event) => updateFromClientX(event.clientX);
    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      commitConfidence(getConfidenceFromLevel(current.level - 1).key);
    }

    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      commitConfidence(getConfidenceFromLevel(current.level + 1).key);
    }
  };

  return (
    <div
      ref={shellRef}
      className="absolute bottom-3 left-3 z-20 flex h-[74px] w-[260px] items-center gap-3 rounded-xl px-3 py-2 backdrop-blur-md"
      style={{
        background: p.meterShellBg,
        border: `1px solid ${
          current.key === "NS" ? p.meterShellBorder : confidenceColor.border
        }`,
        boxShadow:
          current.key === "NS"
            ? p.dark
              ? "0 14px 34px rgba(2,6,23,0.38)"
              : "0 14px 28px rgba(15,23,42,0.13)"
            : p.dark
              ? `0 14px 34px rgba(2,6,23,0.38), 0 0 18px ${confidenceColor.soft}`
              : "0 14px 28px rgba(15,23,42,0.13)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={meterRef}
        role="slider"
        tabIndex={0}
        aria-label="Confidence"
        aria-valuemin={0}
        aria-valuemax={3}
        aria-valuenow={current.level}
        aria-valuetext={current.label}
        className="relative h-[58px] w-[108px] shrink-0 cursor-ew-resize overflow-visible outline-none"
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        onMouseLeave={() => setHoveredConfidence(null)}
      >
        <svg
          width="108"
          height="58"
          viewBox="0 0 108 58"
          className="absolute inset-0 overflow-visible"
        >
          <path
            d="M17 45 A37 37 0 0 1 91 45"
            fill="none"
            stroke={p.meterBase}
            strokeWidth="8"
            strokeLinecap="round"
            pathLength="100"
          />

          <path
            d="M17 45 A37 37 0 0 1 91 45"
            fill="none"
            stroke={current.key === "NS" ? "transparent" : activeColor}
            strokeWidth="8"
            strokeLinecap="round"
            pathLength="100"
            strokeDasharray={`${current.dash} 100`}
            style={{
              transition: "stroke-dasharray 180ms ease, stroke 180ms ease",
              filter:
                current.key !== "NS" && p.dark
                  ? `drop-shadow(0 0 7px ${confidenceColor.soft})`
                  : "none",
            }}
          />

          {hovered && hovered.key !== current.key && (
            <path
              d="M17 45 A37 37 0 0 1 91 45"
              fill="none"
              stroke={hovered.key === "NS" ? "transparent" : activeColor}
              strokeWidth="8"
              strokeLinecap="round"
              pathLength="100"
              strokeDasharray={`${hovered.dash} 100`}
              opacity="0.22"
              style={{
                transition: "stroke-dasharray 120ms ease, stroke 120ms ease",
              }}
            />
          )}

          <path
            d="M26 44 A28 28 0 0 1 82 44"
            fill="none"
            stroke={p.meterBaseSoft}
            strokeWidth="1"
            strokeLinecap="round"
          />

          <line
            x1={pivot.x}
            y1={pivot.y}
            x2={needleEnd.x}
            y2={needleEnd.y}
            stroke={current.key === "NS" ? p.meterNeedle : activeColor}
            strokeWidth="2.6"
            strokeLinecap="round"
            style={{
              transition: "x2 180ms ease, y2 180ms ease, stroke 180ms ease",
              filter:
                current.key !== "NS" && p.dark
                  ? `drop-shadow(0 0 6px ${confidenceColor.soft})`
                  : "none",
            }}
          />

          <circle
            cx={pivot.x}
            cy={pivot.y}
            r="7"
            fill={p.meterKnob}
            stroke={
              current.key === "NS" ? p.inputBorder : confidenceColor.border
            }
            strokeWidth="1.2"
          />

          <circle
            cx={pivot.x}
            cy={pivot.y}
            r="2.4"
            fill={current.key === "NS" ? p.textMuted : activeColor}
          />

          {CONFIDENCE_OPTIONS.map((option) => {
            const point = pointFromAngle(option.angle, markRadius);
            const active = option.key === current.key;
            const passed =
              option.level <= current.level && current.key !== "NS";
            const isHovered = hovered?.key === option.key;

            return (
              <g key={option.key}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={active ? 3 : isHovered ? 3.15 : 2}
                  fill={
                    active || passed || isHovered ? activeColor : p.textMuted
                  }
                  opacity={active ? 1 : isHovered ? 0.95 : passed ? 0.82 : 0.42}
                  style={{
                    transition:
                      "r 120ms ease, opacity 120ms ease, fill 120ms ease",
                  }}
                />

                <circle
                  cx={point.x}
                  cy={point.y}
                  r={active ? 5.4 : isHovered ? 5.2 : 4.4}
                  fill="none"
                  stroke={
                    active || isHovered
                      ? current.key === "NS"
                        ? p.meterShellBorder
                        : confidenceColor.border
                      : "transparent"
                  }
                  strokeWidth="1"
                  opacity={active ? 1 : isHovered ? 0.75 : 0}
                  style={{
                    transition:
                      "r 120ms ease, opacity 120ms ease, stroke 120ms ease",
                  }}
                />
              </g>
            );
          })}
        </svg>

        <div className="absolute inset-0 grid grid-cols-4">
          {CONFIDENCE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className="h-full w-full"
              title={option.label}
              aria-label={`Set confidence to ${option.label}`}
              onMouseEnter={() => setHoveredConfidence(option.key)}
              onFocus={() => setHoveredConfidence(option.key)}
              onMouseLeave={() => setHoveredConfidence(null)}
              onBlur={() => setHoveredConfidence(null)}
              onClick={(e) => {
                e.stopPropagation();
                commitConfidence(option.key);
              }}
            />
          ))}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div
            className="text-[10px] uppercase tracking-[0.16em]"
            style={{ color: p.textMuted }}
          >
            Confidence
          </div>

          {onAddNote && (
            <button
              type="button"
              title="Add note without changing confidence"
              aria-label="Add note without changing confidence"
              className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full outline-none transition-[border-color,background-color,color] duration-200"
              style={{
                color: isNoteButtonHovered ? p.activeText : p.textMuted,
                background: isNoteButtonHovered
                  ? p.dark
                    ? "rgba(14,165,233,0.075)"
                    : "rgba(14,165,233,0.045)"
                  : p.dark
                    ? "rgba(148,163,184,0.055)"
                    : "rgba(100,116,139,0.045)",
                border: `1px solid ${
                  isNoteButtonHovered ? p.activeBorder : p.inputBorder
                }`,
              }}
              onMouseEnter={() => setIsNoteButtonHovered(true)}
              onMouseLeave={() => setIsNoteButtonHovered(false)}
              onFocus={() => setIsNoteButtonHovered(true)}
              onBlur={() => setIsNoteButtonHovered(false)}
              onClick={(event) => {
                event.stopPropagation();
                setIsNoteButtonHovered(false);
                onAddNote();
              }}
            >
              <Plus size={11} strokeWidth={2.35} />
            </button>
          )}
        </div>

        <div
          className="mt-1 text-[15px] leading-none"
          style={{ color: confidenceColor.text }}
        >
          {current.label}
        </div>

        <div
          className="mt-2 flex items-center gap-1.5"
          onMouseLeave={() => setHoveredConfidence(null)}
        >
          {CONFIDENCE_OPTIONS.map((option) => {
            const active = option.key === current.key;
            const passed =
              option.level <= current.level && current.key !== "NS";
            const isHovered = hovered?.key === option.key;

            return (
              <button
                key={option.key}
                type="button"
                className="h-1.5 flex-1 rounded-full transition-all"
                title={option.label}
                aria-label={`Set confidence to ${option.label}`}
                style={{
                  background: active || passed ? activeColor : p.meterBase,
                  opacity: active ? 1 : isHovered ? 0.95 : passed ? 0.82 : 0.58,
                  boxShadow: isHovered
                    ? `0 0 0 3px ${confidenceColor.soft}`
                    : "none",
                  transform: isHovered ? "scaleY(1.45)" : "scaleY(1)",
                }}
                onMouseEnter={() => setHoveredConfidence(option.key)}
                onFocus={() => setHoveredConfidence(option.key)}
                onMouseLeave={() => setHoveredConfidence(null)}
                onBlur={() => setHoveredConfidence(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  commitConfidence(option.key);
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const FeedCard = ({
  id,
  article,
  t,
  onCardClick,
  isActive,
  typeBadge,
  onCardInteract,
}) => {
  const p = useMemo(() => getPalette(t), [t]);
  const merged = useMemo(() => getArticle(article || {}), [article]);
  const { fullName } = useUserContext();
  const feedbackUserName = fullName || "User";
  const [screenshotBroken, setScreenshotBroken] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showUpdatedToast, setShowUpdatedToast] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [toastMessage, setToastMessage] = useState("Updated");
  const [feedbackDraft, setFeedbackDraft] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [isSavingFeedbackNote, setIsSavingFeedbackNote] = useState(false);
  const toastTimerRef = useRef(null);
  const feedbackComposerRef = useRef(null);
  const meterShellRef = useRef(null);
  const pendingDraftRef = useRef(null);
  const feedbackUserNameRef = useRef(feedbackUserName);

  const screenshot = getScreenshot(merged);
  const documentId = merged.document_id || merged.documentId || merged.id;
  const productId =
    merged.product_id || merged.productId || merged.product_document_id || null;
  const showConfidenceControls = canAdjustConfidence(merged);
  const mainText =
    merged.reasoning ||
    merged.description ||
    merged.title ||
    merged.product_name ||
    "No summary available.";

  useEffect(() => {
    setScreenshotBroken(false);
  }, [screenshot]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!feedbackDraft) return undefined;

    const handlePointerDown = (event) => {
      if (feedbackComposerRef.current?.contains(event.target)) return;
      // Interacting with the meter while the composer is open updates the
      // pending draft instead of committing it.
      if (meterShellRef.current?.contains(event.target)) return;

      dismissFeedbackComposer();
    };

    window.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [feedbackDraft]);

  useEffect(() => {
    pendingDraftRef.current =
      feedbackDraft?.mode === "confidence_change" ? feedbackDraft : null;
  }, [feedbackDraft]);

  useEffect(() => {
    feedbackUserNameRef.current = feedbackUserName;
  }, [feedbackUserName]);

  // Auto-expand the history section when a transaction is added this session,
  // so the new entry (and its slide-in) is actually visible.
  const historyCount = merged.feedback.length;
  const prevHistoryCountRef = useRef(historyCount);

  useEffect(() => {
    if (historyCount > prevHistoryCountRef.current) {
      setHistoryOpen(true);
    }
    prevHistoryCountRef.current = historyCount;
  }, [historyCount]);

  // If the card unmounts (feed refresh, filter change) with a confidence
  // change still pending behind the composer, flush it so it isn't lost.
  useEffect(() => {
    return () => {
      const draft = pendingDraftRef.current;
      if (!draft) return;

      toggleProductConfidence({
        action: getConfidenceOption(draft.nextTier).action,
        document_id: draft.documentId,
        feedback: buildFeedbackEntry(draft, "", feedbackUserNameRef.current),
      })?.catch?.(() => {});
    };
  }, []);

  const showMeterToast = (error = false, message = "Updated") => {
    setToastError(error);
    setToastMessage(message);
    setShowUpdatedToast(true);

    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = window.setTimeout(() => {
      setShowUpdatedToast(false);
      setToastError(false);
      setToastMessage("Updated");
    }, 1200);
  };

  const patchCardState = (nextState) => {
    if (!onCardInteract) return;

    onCardInteract(id, documentId, {
      like: merged.like,
      dislike: merged.dislike,
      catalog: merged.catalog,
      tier_label: merged.tier_label,
      ...nextState,
    });
  };

  const handleCatalogToggle = (e) => {
    e.stopPropagation();
    if (!documentId) return;

    // Persist the bookmark server-side (same call ArticleCard and the detail
    // panel make), then optimistically patch the card state.
    toggleThumb({ document_id: documentId, action: "catalog" })?.catch?.(
      () => {},
    );

    patchCardState({
      catalog: !merged.catalog,
    });
  };

  const handleAddConfidenceNote = () => {
    if (!showConfidenceControls || !documentId) return;

    const current = getConfidenceOption(merged.tier_label);

    setFeedbackDraft({
      mode: "note_only",
      documentId,
      productId,
      previousTier: current.key,
      nextTier: current.key,
      previousLabel: current.label,
      nextLabel: current.label,
    });
    setFeedbackNote("");
  };

  // Confidence changes are debounced behind the feedback composer: moving the
  // meter only patches the UI and opens/updates a pending draft. The single
  // toggleProductConfidence call fires when the composer closes — with the
  // note if one was saved, without it otherwise.
  const handleConfidenceChange = (tierLabelKey) => {
    if (!showConfidenceControls || !documentId) return;

    const next = getConfidenceOption(tierLabelKey);
    const displayed = getConfidenceOption(merged.tier_label);

    if (next.key === displayed.key) return;

    // If a change is already pending, keep measuring from where the
    // interaction started, not from the optimistic value.
    const previous = getConfidenceOption(
      feedbackDraft?.mode === "confidence_change"
        ? feedbackDraft.previousTier
        : merged.tier_label,
    );

    patchCardState({
      tier_label: next.key,
    });

    if (next.key === previous.key) {
      // Moved back to the starting tier — nothing to record.
      setFeedbackDraft(null);
      setFeedbackNote("");
      return;
    }

    setFeedbackDraft({
      mode: "confidence_change",
      documentId,
      productId,
      previousTier: previous.key,
      nextTier: next.key,
      previousLabel: previous.label,
      nextLabel: next.label,
    });
  };

  const commitConfidenceFeedback = async (draft, note = "") => {
    const isNoteOnly = draft.mode === "note_only";

    if (isNoteOnly && !note.trim()) return;

    const feedbackEntry = buildFeedbackEntry(draft, note, feedbackUserName);

    try {
      await toggleProductConfidence({
        action: getConfidenceOption(draft.nextTier).action,
        document_id: draft.documentId,
        feedback: feedbackEntry,
      });

      // Reflect the new transaction in the history block right away —
      // entries are stored oldest-first, so append.
      patchCardState({
        ...(isNoteOnly ? {} : { tier_label: draft.nextTier }),
        feedback: [...merged.feedback, feedbackEntry],
      });

      showMeterToast(false, isNoteOnly ? "Note saved" : "Updated");
    } catch (err) {
      console.error("[FeedCard] toggleProductConfidence failed:", err);

      if (!isNoteOnly) {
        patchCardState({
          tier_label: draft.previousTier,
        });
      }

      showMeterToast(true);
    }
  };

  const dismissFeedbackComposer = () => {
    if (isSavingFeedbackNote) return;

    const draft = feedbackDraft;

    setFeedbackDraft(null);
    setFeedbackNote("");

    if (draft?.mode === "confidence_change") {
      commitConfidenceFeedback(draft);
    }
  };

  const handleFeedbackSubmit = async (event) => {
    event?.preventDefault?.();

    const note = feedbackNote.trim();

    if (!showConfidenceControls || !feedbackDraft || !note) {
      dismissFeedbackComposer();
      return;
    }

    const draft = feedbackDraft;

    setIsSavingFeedbackNote(true);

    try {
      await commitConfidenceFeedback(draft, note);
      setFeedbackDraft(null);
      setFeedbackNote("");
    } finally {
      setIsSavingFeedbackNote(false);
    }
  };

  return (
    <article
      className={cn(
        "group relative self-start cursor-pointer overflow-hidden rounded-2xl transition-all duration-200",
        isActive ? "scale-[1.005]" : "hover:-translate-y-0.5",
      )}
      onClick={() => onCardClick(id, merged)}
      style={{
        background: p.cardBg,
        border: `1px solid ${isActive ? p.activeBorder : p.cardBorder}`,
        boxShadow: p.cardShadow,
      }}
    >
      <div className="flex h-full flex-col">
        <div
          className="relative aspect-[16/9] w-full overflow-hidden border-b"
          style={{ borderColor: p.divider }}
        >
          {screenshot && !screenshotBroken ? (
            <img
              src={screenshot}
              alt=""
              className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.015]"
              onError={() => setScreenshotBroken(true)}
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{ background: p.screenshotFallback }}
            >
              <div
                className="flex flex-col items-center gap-2 text-xs"
                style={{ color: p.textMuted }}
              >
                <ImageOff size={26} />
                No screenshot available
              </div>
            </div>
          )}

          {showConfidenceControls && (
            <MeterToast
              show={showUpdatedToast}
              p={p}
              error={toastError}
              message={toastMessage}
            />
          )}

          <BookmarkButton
            active={merged.catalog}
            p={p}
            onClick={handleCatalogToggle}
          />

          {showConfidenceControls && (
            <>
              <ConfidenceMeter
                value={merged.tier_label}
                p={p}
                onChange={handleConfidenceChange}
                onAddNote={handleAddConfidenceNote}
                shellRef={meterShellRef}
              />

              <ConfidenceFeedbackComposer
                draft={feedbackDraft}
                note={feedbackNote}
                p={p}
                isSaving={isSavingFeedbackNote}
                composerRef={feedbackComposerRef}
                onChange={setFeedbackNote}
                onDismiss={dismissFeedbackComposer}
                onSubmit={handleFeedbackSubmit}
              />
            </>
          )}

          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-28"
            style={{ background: p.imageGradient }}
          />
        </div>

        <div className="flex flex-1 flex-col gap-3 p-4">
          {typeBadge && (
            <div className="flex flex-wrap gap-1.5">{typeBadge}</div>
          )}

          <div>
            <p
              className={cn(
                "text-[15px] leading-relaxed",
                expandedReasoning ? "" : "line-clamp-3",
              )}
              style={{ color: p.textPrimary }}
            >
              {mainText}
            </p>

            {mainText.length > 190 && (
              <button
                type="button"
                className="mt-1 text-xs transition-opacity hover:opacity-80"
                style={{ color: p.activeText }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedReasoning((v) => !v);
                }}
              >
                {expandedReasoning ? "Show less" : "Read more"}
              </button>
            )}
          </div>

          {merged.recommended_action && (
            <DetailBlock
              icon={Bookmark}
              label="Recommended action"
              p={p}
              strong
            >
              {merged.recommended_action}
            </DetailBlock>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-wrap gap-1.5">
                <ClassificationBadge value={merged.classification} p={p} />
              </div>

              {showConfidenceControls && (
                <HistoryTrigger
                  count={historyCount}
                  open={historyOpen}
                  p={p}
                  onToggle={() => setHistoryOpen((v) => !v)}
                />
              )}
            </div>

            {Array.isArray(merged.flags) && merged.flags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {merged.flags.slice(0, 9).map((flag) => (
                  <FlagBadge key={flag} p={p}>
                    {formatFlag(flag)}
                  </FlagBadge>
                ))}

                {merged.flags.length > 9 && (
                  <FlagBadge p={p}>+{merged.flags.length - 9}_more</FlagBadge>
                )}
              </div>
            )}

            {showConfidenceControls && (
              <div
                className="grid transition-[grid-template-rows,margin-top] duration-300 ease-out"
                style={{
                  gridTemplateRows: historyOpen ? "1fr" : "0fr",
                  // Cancel the parent's gap while collapsed so the zero-height
                  // wrapper doesn't leave a phantom 8px of spacing.
                  marginTop: historyOpen ? "0px" : "-8px",
                }}
              >
                <div className="min-h-0 overflow-hidden">
                  <HistoryBlock entries={merged.feedback} p={p} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export const ScrollSentinel = ({ onIntersect, hasMore, isLoadingMore, t }) => {
  const ref = useRef(null);
  const p = getPalette(t);

  useEffect(() => {
    if (!ref.current || !hasMore) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          onIntersect();
        }
      },
      { rootMargin: "400px" },
    );

    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [hasMore, isLoadingMore, onIntersect]);

  return (
    <div ref={ref} className="w-full flex justify-center py-8">
      {isLoadingMore && hasMore && <LoadingSpinner />}

      {!hasMore && (
        <p
          className="text-sm"
          style={{
            color: p.textMuted,
          }}
        >
          You've reached the end
        </p>
      )}
    </div>
  );
};
